import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, fmtErr, img } from "@/lib/api";

const inputCls = "mt-1.5 w-full rounded-sm border border-[#3b1d63] bg-[#07030c] px-3 py-2.5 text-sm text-[#f3eefc] outline-none focus:border-[#a855f7]";
const CATS = ["Hoodies", "Tops", "Outerwear", "Denim", "Footwear", "Fragrance", "Bags", "Accessories", "Watches"];

export const ListingDialog = ({ product, onClose, onSaved }) => {
  const isNew = !product.id;
  const [f, setF] = useState({
    title: product.title || "", brand: product.brand || "", category: product.category || "Hoodies",
    size: product.size || "", price: product.price ?? "", condition: product.condition || "",
    description: product.description || "", images: product.images || [], status: product.status || "Available",
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const field = (k, label, props = {}) => (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-[#a78bce]">{label}</span>
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`listing-${k}-input`} {...props} />
    </label>
  );

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        if (data?.url) urls.push(data.url);
      }
      setF((s) => ({ ...s, images: [...s.images, ...urls] }));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, price: Number(f.price) };
      if (isNew) await api.post("/admin/products", body);
      else await api.put(`/admin/products/${product.id}`, body);
      toast.success(isNew ? "Listing published" : "Listing updated");
      onSaved();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent className="max-h-[92vh] overflow-y-auto border-[#3b1d63] bg-[#0b0514] text-[#f3eefc] sm:max-w-2xl" data-testid="listing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">{isNew ? "New listing" : "Edit listing"}</DialogTitle>
          <DialogDescription className="text-[#cbbbe6]">Upload photos straight from your phone or computer.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="flex flex-wrap gap-3">
            {f.images.map((s, i) => (
              <div key={s + i} className="relative h-24 w-20">
                <img src={img(s)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setF({ ...f, images: f.images.filter((_, k) => k !== i) })} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-[#07030c] text-white ring-1 ring-[#3b1d63]" aria-label="Remove photo" data-testid={`listing-remove-image-${i}`}><X className="h-3 w-3" /></button>
              </div>
            ))}
            <label className="grid h-24 w-20 cursor-pointer place-items-center border border-dashed border-[#3b1d63] text-[#a78bce] transition-colors hover:border-[#a855f7]" data-testid="listing-upload-label">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
              <input type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="listing-upload-input" />
            </label>
          </div>
            {field("title", "Title", { required: true })}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-[#a78bce]">Category</span>
              <input list="cats" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} required className={inputCls} data-testid="listing-category-input" />
              <datalist id="cats">{CATS.map((c) => <option key={c} value={c} />)}</datalist>
            </label>
            {field("size", "Size")}
            {field("price", "Price (CAD)", { type: "number", min: 0, step: "1", required: true })}
            {field("condition", "Condition")}
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-[#a78bce]">Status</span>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls} data-testid="listing-status-select">
                {["Available", "Reserved", "Sold"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-[#a78bce]">Description</span>
            <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} data-testid="listing-description-input" />
          </label>
          <button disabled={busy || uploading} className="w-full rounded-full bg-[#7c3aed] py-3.5 text-sm font-medium transition-colors hover:bg-[#a855f7] disabled:opacity-60" data-testid="listing-save-button">
            {busy ? "Saving…" : isNew ? "Publish listing" : "Save changes"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
